import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo, Loader } from "@/components/Brand";
import { formatCurrency } from "@/lib/format";
import { visibleQuestions, isAnswered } from "@/lib/quote";
import { useAuth } from "@/context/AuthContext";

function OptionCard({ selected, label, description, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex min-h-[64px] w-full items-center justify-between rounded-xl border-2 p-5 text-left transition-all duration-200 active:scale-[0.99] ${
        selected
          ? "border-amber-500 bg-amber-500/10 font-semibold text-white shadow-[0_0_20px_rgba(217,119,6,0.2)]"
          : "border-slate-800 bg-slate-900/60 text-slate-200 hover:border-slate-600 hover:bg-slate-800/70"}`}>
      <span className="flex flex-col">
        <span>{label}</span>
        {description ? <span className="mt-0.5 text-xs font-normal text-slate-400">{description}</span> : null}
      </span>
      <span className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
        selected ? "border-amber-500 bg-amber-500 text-slate-950" : "border-slate-600"}`}>
        {selected && <Check className="h-3.5 w-3.5" />}
      </span>
    </button>
  );
}

export default function Quote({ preview = false }) {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const previewMode = preview || searchParams.get("preview") === "1";
  const navigate = useNavigate();
  const { user, register } = useAuth();

  const [data, setData] = useState(null);
  const [projectTypeId, setProjectTypeId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0); // 0 = choose type, 1..N questions, then review, then register
  const [estimate, setEstimate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/categories/${categoryId}/questionnaire`)
      .then(({ data }) => setData(data))
      .catch(() => { toast.error("Could not load questionnaire"); navigate("/"); });
  }, [categoryId, navigate]);

  const questions = useMemo(() => data?.questions || [], [data]);
  const flow = useMemo(() => visibleQuestions(questions, answers), [questions, answers]);
  const totalSteps = flow.length + 2; // +choose type +review
  const projectType = data?.project_types.find((t) => t.id === projectTypeId);

  const fetchEstimate = useCallback(async () => {
    if (!projectTypeId) return;
    try {
      const { data: est } = await api.post("/quotations/estimate", {
        category_id: categoryId, project_type_id: projectTypeId, answers });
      setEstimate(est);
    } catch { /* ignore */ }
  }, [categoryId, projectTypeId, answers]);

  useEffect(() => { fetchEstimate(); }, [fetchEstimate]);

  if (!data) return <div className="min-h-screen bg-[#090A0C]"><Loader label="Preparing your questionnaire" /></div>;

  const showLiveEstimate = data.show_live_estimate && projectTypeId;
  // step mapping: 0 => type; 1..flow.length => flow[step-1]; flow.length+1 => review; flow.length+2 => register
  const isTypeStep = step === 0;
  const isReviewStep = step === flow.length + 1;
  const isRegisterStep = step === flow.length + 2;
  const currentQuestion = !isTypeStep && !isReviewStep && !isRegisterStep ? flow[step - 1] : null;
  const progress = Math.min(100, Math.round((step / (totalSteps)) * 100));

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const canContinue = () => {
    if (isTypeStep) return !!projectTypeId;
    if (currentQuestion) return isAnswered(currentQuestion, answers);
    return true;
  };

  const goNext = () => {
    if (isReviewStep) {
      if (previewMode) { toast.success("Preview complete — this is what the client sees."); return; }
      if (user && user.role === "client") { submitQuotation(); return; }
      setStep(step + 1); // to register
      return;
    }
    setStep(step + 1);
  };
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const submitQuotation = async () => {
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/quotations", {
        category_id: categoryId, project_type_id: projectTypeId, answers });
      toast.success("Quotation generated");
      navigate(`/quotation/${res.quotation.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0C] text-slate-50">
      <header className="border-b border-slate-800/80">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-4">
          <Logo size="text-lg" />
          <button onClick={() => navigate("/")} className="text-sm text-slate-400 hover:text-amber-400" data-testid="quote-exit-btn">Exit</button>
        </div>
      </header>

      {/* Progress */}
      <div className="mx-auto max-w-2xl px-4 pt-6">
        <div className="mb-2 flex items-center justify-between text-xs font-medium text-slate-400">
          <span data-testid="questionnaire-step-indicator">
            Step {step + 1} of {totalSteps}
          </span>
          <span>{progress}% complete</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800" data-testid="questionnaire-progress-bar">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all duration-300"
            style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Live estimate */}
      {showLiveEstimate && !isRegisterStep && estimate && (
        <div className="mx-auto mt-6 max-w-2xl px-4">
          <div className="flex items-center justify-between rounded-2xl border border-amber-500/30 bg-[#090A0C]/90 p-4 shadow-2xl backdrop-blur-xl" data-testid="live-estimate-badge">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Current estimated project value</span>
            <span className="font-mono text-2xl font-bold tracking-wider text-amber-500">{formatCurrency(estimate.total)}</span>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="mx-auto max-w-2xl px-4 py-8 pb-32">
        {previewMode && (
          <div className="mb-5 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-center text-xs font-semibold text-amber-400">
            ADMIN PREVIEW — no account or quotation will be saved
          </div>
        )}

        {isTypeStep && (
          <div className="evo-fade-up">
            <h2 className="font-heading text-2xl font-bold sm:text-3xl">
              {categoryId === "loft-conversion"
                ? "What type of loft conversion are you considering?"
                : "What type of property extension are you considering?"}
            </h2>
            <p className="mt-2 text-sm text-slate-400">Select the option closest to your project. You can refine later.</p>
            <div className="mt-6 grid grid-cols-1 gap-3">
              {data.project_types.map((t) => (
                <OptionCard key={t.id} selected={projectTypeId === t.id}
                  label={t.name} onClick={() => setProjectTypeId(t.id)}
                  testid={`project-type-option-${t.id}`} />
              ))}
            </div>
          </div>
        )}

        {currentQuestion && (
          <QuestionView key={currentQuestion.id} question={currentQuestion}
            value={answers[currentQuestion.id]} onChange={(v) => setAnswer(currentQuestion.id, v)} />
        )}

        {isReviewStep && (
          <ReviewView data={data} projectType={projectType} answers={answers}
            estimate={estimate} flow={flow}
            onEdit={(qIndex) => setStep(qIndex + 1)} onEditType={() => setStep(0)} />
        )}

        {isRegisterStep && (
          <RegisterView register={register} onDone={submitQuotation} submitting={submitting} />
        )}
      </div>

      {/* Sticky footer nav */}
      {!isRegisterStep && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-800 bg-[#090A0C]/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4">
            {step > 0 && (
              <button onClick={goBack}
                className="flex items-center justify-center gap-2 rounded-xl border border-slate-700 px-5 py-3.5 font-semibold text-slate-200 hover:border-slate-500"
                data-testid="questionnaire-back-btn">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
            <button onClick={goNext} disabled={!canContinue() || submitting}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 px-8 py-3.5 text-lg font-bold text-slate-950 shadow-lg shadow-amber-500/20 transition-all hover:from-amber-500 hover:to-amber-400 active:scale-95 disabled:opacity-40"
              data-testid="questionnaire-continue-btn">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> :
                isReviewStep ? (previewMode ? "Finish preview" : (user?.role === "client" ? "Get my quotation" : "Continue to register")) : "Continue"}
              {!submitting && <ArrowRight className="h-5 w-5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionView({ question, value, onChange }) {
  const q = question;
  return (
    <div className="evo-fade-up">
      <span className="text-xs font-semibold uppercase tracking-wider text-amber-500">{q.section}</span>
      <h2 className="mt-1.5 font-heading text-2xl font-bold sm:text-3xl">{q.title}</h2>
      {q.description && <p className="mt-2 text-sm text-slate-400">{q.description}</p>}

      <div className="mt-6">
        {["single_choice", "dropdown", "yes_no", "radio"].includes(q.type) && (
          <div className="grid grid-cols-1 gap-3">
            {q.options.sort((a, b) => a.order - b.order).map((o) => (
              <OptionCard key={o.id} selected={value === o.id} label={o.label} description={o.description}
                onClick={() => onChange(o.id)} testid={`question-option-${o.id}`} />
            ))}
          </div>
        )}

        {["multiple_choice", "checkbox"].includes(q.type) && (
          <div className="grid grid-cols-1 gap-3">
            {q.options.sort((a, b) => a.order - b.order).map((o) => {
              const arr = Array.isArray(value) ? value : [];
              const sel = arr.includes(o.id);
              return (
                <OptionCard key={o.id} selected={sel} label={o.label}
                  onClick={() => onChange(sel ? arr.filter((x) => x !== o.id) : [...arr, o.id])}
                  testid={`question-option-${o.id}`} />
              );
            })}
          </div>
        )}

        {["number", "currency", "measurement", "slider"].includes(q.type) && (
          <div className="relative">
            <input type="number" min="0" step="any" value={value ?? ""} autoFocus
              onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
              className="w-full rounded-xl border-2 border-slate-800 bg-slate-900/60 px-5 py-5 text-2xl font-semibold text-white outline-none focus:border-amber-500"
              placeholder="0" data-testid="question-number-input" />
            {q.unit && <span className="absolute right-5 top-1/2 -translate-y-1/2 font-mono text-sm text-slate-400">{q.unit}</span>}
          </div>
        )}

        {["text", "long_text"].includes(q.type) && (
          q.type === "long_text" ? (
            <textarea rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
              className="w-full rounded-xl border-2 border-slate-800 bg-slate-900/60 px-5 py-4 text-white outline-none focus:border-amber-500"
              data-testid="question-text-input" />
          ) : (
            <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
              className="w-full rounded-xl border-2 border-slate-800 bg-slate-900/60 px-5 py-4 text-white outline-none focus:border-amber-500"
              data-testid="question-text-input" />
          )
        )}
      </div>
    </div>
  );
}

function answerLabel(q, v) {
  if (v === undefined || v === null || v === "") return "—";
  if (["single_choice", "dropdown", "yes_no", "radio"].includes(q.type)) {
    return q.options.find((o) => o.id === v)?.label || String(v);
  }
  if (["multiple_choice", "checkbox"].includes(q.type)) {
    return q.options.filter((o) => (v || []).includes(o.id)).map((o) => o.label).join(", ") || "—";
  }
  return `${v}${q.unit ? " " + q.unit : ""}`;
}

function ReviewView({ data, projectType, answers, estimate, flow, onEdit, onEditType }) {
  const answered = flow.filter((q) => answers[q.id] !== undefined && answers[q.id] !== "" &&
    !(Array.isArray(answers[q.id]) && answers[q.id].length === 0));
  return (
    <div className="evo-fade-up">
      <h2 className="font-heading text-2xl font-bold sm:text-3xl">Review your project</h2>
      <p className="mt-2 text-sm text-slate-400">Check your answers below. Tap edit to change anything.</p>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-800 bg-[#151820]">
        <div className="flex items-center justify-between border-b border-slate-800 p-4" data-testid="review-editable-section">
          <div>
            <span className="text-xs uppercase tracking-wide text-slate-500">{data.category.name}</span>
            <p className="font-heading text-lg font-semibold">{projectType?.name}</p>
          </div>
          <button onClick={onEditType} className="flex items-center gap-1.5 text-sm font-medium text-amber-400 hover:underline" data-testid="review-edit-type">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        </div>
        {answered.map((q) => (
          <div key={q.id} className="flex items-center justify-between border-b border-slate-800/60 p-4 last:border-0" data-testid="review-editable-section">
            <div className="pr-3">
              <span className="text-xs text-slate-500">{q.title}</span>
              <p className="text-sm font-medium text-slate-100">{answerLabel(q, answers[q.id])}</p>
            </div>
            <button onClick={() => onEdit(flow.indexOf(q))} className="flex items-center gap-1.5 text-sm font-medium text-amber-400 hover:underline" data-testid={`review-edit-${q.id}`}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          </div>
        ))}
      </div>

      {data.show_live_estimate && estimate && (
        <div className="mt-6 rounded-2xl border border-amber-500/30 bg-gradient-to-b from-amber-500/10 to-transparent p-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-400">Your estimated project quotation</p>
          <p className="mt-2 font-mono text-4xl font-extrabold text-amber-500" data-testid="review-estimate-total">{formatCurrency(estimate.total)}</p>
          <p className="mt-3 text-xs leading-relaxed text-slate-400">{data.disclaimer}</p>
        </div>
      )}
      {!data.show_live_estimate && (
        <div className="mt-6 rounded-2xl border border-slate-800 bg-[#111318] p-6 text-center">
          <p className="text-sm font-semibold text-slate-200">Create your account on the next step to view your estimated quotation.</p>
          <p className="mt-2 text-xs leading-relaxed text-slate-400">{data.disclaimer}</p>
        </div>
      )}
    </div>
  );
}

function RegisterView({ register, onDone, submitting }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", confirm: "",
    line1: "", line2: "", city: "", county: "", postcode: "", country: "United Kingdom",
    agree: false, marketing: false,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    if (!form.agree) return toast.error("Please accept the Terms & Conditions");
    setLoading(true);
    try {
      await register({
        name: form.name, email: form.email, password: form.password, phone: form.phone,
        marketing_opt_in: form.marketing,
        address: { line1: form.line1, line2: form.line2, city: form.city,
          county: form.county, postcode: form.postcode, country: form.country },
      });
      await onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const inp = "w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-4 py-3 text-slate-100 outline-none focus:border-amber-500";
  return (
    <div className="evo-fade-up">
      <h2 className="font-heading text-2xl font-bold sm:text-3xl">Create Your EvoStruct Account</h2>
      <p className="mt-2 text-sm text-slate-400">Create your account to view your quotation and keep all your project information in one place.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <input required placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="register-name-input" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="register-email-input" />
          <input required placeholder="Phone number" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="register-phone-input" />
        </div>
        <input required placeholder="Address line 1" value={form.line1} onChange={(e) => set("line1", e.target.value)} className={inp} data-testid="register-address1-input" />
        <input placeholder="Address line 2 (optional)" value={form.line2} onChange={(e) => set("line2", e.target.value)} className={inp} data-testid="register-address2-input" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input required placeholder="Town / City" value={form.city} onChange={(e) => set("city", e.target.value)} className={inp} data-testid="register-city-input" />
          <input placeholder="County" value={form.county} onChange={(e) => set("county", e.target.value)} className={inp} data-testid="register-county-input" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input required placeholder="Postcode" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className={inp} data-testid="register-postcode-input" />
          <input placeholder="Country" value={form.country} onChange={(e) => set("country", e.target.value)} className={inp} data-testid="register-country-input" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input required type="password" placeholder="Password" value={form.password} onChange={(e) => set("password", e.target.value)} className={inp} data-testid="register-password-input" />
          <input required type="password" placeholder="Confirm password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} className={inp} data-testid="register-confirm-input" />
        </div>
        <label className="flex items-start gap-3 text-sm text-slate-300">
          <input type="checkbox" checked={form.agree} onChange={(e) => set("agree", e.target.checked)} className="mt-1 h-4 w-4 accent-amber-500" data-testid="register-terms-checkbox" />
          <span>I agree to the Terms &amp; Conditions and Privacy Policy.</span>
        </label>
        <label className="flex items-start gap-3 text-sm text-slate-400">
          <input type="checkbox" checked={form.marketing} onChange={(e) => set("marketing", e.target.checked)} className="mt-1 h-4 w-4 accent-amber-500" data-testid="register-marketing-checkbox" />
          <span>I would like to receive updates and information from EvoStruct.</span>
        </label>
        <button type="submit" disabled={loading || submitting}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 px-8 py-4 text-lg font-bold text-slate-950 shadow-lg transition-all hover:from-amber-500 hover:to-amber-400 active:scale-95 disabled:opacity-50"
          data-testid="register-form-submit-btn">
          {(loading || submitting) ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account & view quotation"}
        </button>
      </form>
    </div>
  );
}
